import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { MapPin, Star, Shield, ShieldCheck, Calendar, User as UserIcon, Briefcase, Clock, DollarSign, CheckCircle, XCircle, Edit3, Trash2, Image, X, AlertTriangle, Ban, ArrowLeft } from 'lucide-react';
import { useWSEvent, useWebSocket } from '../contexts/WebSocketContext';
import ReportBlockControls from '../components/moderation/ReportBlockControls';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Inquiries = ({ user, onLogout }) => {
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get('conversation');
  const clientParam = searchParams.get('client');
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const supportScrollRef = useRef(null);
  const initialLoadRef = useRef(false);
  const prependingRef = useRef(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [oldestCursor, setOldestCursor] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  // Details panel state
  const [otherUserDetails, setOtherUserDetails] = useState(null);
  const [latestReview, setLatestReview] = useState(null);
  const [relatedBooking, setRelatedBooking] = useState(null);
  const [hasTransaction, setHasTransaction] = useState(false);
  const [relatedService, setRelatedService] = useState(null);
  const [relatedJobBid, setRelatedJobBid] = useState(null);
  const [relatedJob, setRelatedJob] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Open-Time service agreement
  const [agreement, setAgreement] = useState(null);
  const [agreementForm, setAgreementForm] = useState({ start_date: '', start_time: '', end_time: '', agreed_amount: '' });
  const [showAgreementForm, setShowAgreementForm] = useState(false);
  const [savingAgreement, setSavingAgreement] = useState(false);

  // Modify modal state
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyData, setModifyData] = useState({
    proposed_amount: '',
    proposed_date: '',
    proposed_time: '',
    notes: ''
  });
  const [processingAction, setProcessingAction] = useState(false);

  // Violation warning state
  const [violationWarning, setViolationWarning] = useState(null);
  const [showViolationModal, setShowViolationModal] = useState(false);

  // Support tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedSupportTicket, setSelectedSupportTicket] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportSending, setSupportSending] = useState(false);
  const [newSupportMessage, setNewSupportMessage] = useState('');
  const [supportExpanded, setSupportExpanded] = useState(true);
  const [showClosedTickets, setShowClosedTickets] = useState(false);
  const supportPollRef = useRef(null);

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [viewerImage, setViewerImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Delete conversation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(false);

  // WebSocket real-time state
  const { send: wsSend, isConnected: wsConnected, isUserOnline, checkOnlineStatus } = useWebSocket();
  const [typingUser, setTypingUser] = useState(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const effectiveRole = user?.active_role || user?.role;
  // Fee rates - will be fetched from platform config
  const [feeRates, setFeeRates] = useState({
    client_fee_rate: 10,  // Default 10%
    provider_fee_rate: 5  // Default 5%
  });

  // Fetch platform fee rates
  const fetchFeeRates = async () => {
    try {
      const response = await axios.get(`${API}/fee-rates`);
      setFeeRates({
        client_fee_rate: response.data.client_fee_rate || 10,
        provider_fee_rate: response.data.provider_fee_rate || 5
      });
    } catch (error) {
      console.error('Error fetching fee rates:', error);
    }
  };

  // WebSocket: receive new messages in real-time
  useWSEvent('chat_message', (event) => {
    const d = event.data;
    if (!d || !selectedInquiry) return;
    // Only append if message belongs to the currently open thread
    if (d.thread_id === selectedInquiry.id && d.sender_id !== user?.id) {
      setMessages(prev => {
        if (prev.some(m => m.id === d.id)) return prev;
        return [...prev, d];
      });
      // Auto-scroll (messages column only)
      setTimeout(() => scrollMessagesToBottom(true), 100);
    }
    // Refresh sidebar list
    fetchInquiries();
  });

  // WebSocket: typing indicator
  useWSEvent('typing', (event) => {
    if (selectedInquiry && event.thread_id === selectedInquiry.id && event.sender_id !== user?.id) {
      setTypingUser(event.sender_name);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
    }
  });

  useWSEvent('stop_typing', (event) => {
    if (selectedInquiry && event.thread_id === selectedInquiry.id) {
      setTypingUser(null);
    }
  });

  // WebSocket: read receipts
  useWSEvent('messages_read', (event) => {
    if (selectedInquiry && event.thread_id === selectedInquiry.id) {
      setMessages(prev => prev.map(m => m.sender_id === user?.id ? { ...m, read: true } : m));
    }
  });

  // Send typing indicator (throttled)
  const emitTyping = () => {
    if (!wsConnected || !selectedInquiry) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    const otherUserId = selectedInquiry.provider_id === user?.id ? selectedInquiry.client_id : selectedInquiry.provider_id;
    wsSend({
      type: 'typing',
      recipient_id: otherUserId,
      thread_id: selectedInquiry.id,
      thread_type: selectedInquiry.inquiry_type || 'inquiry',
    });
  };

  useEffect(() => {
    fetchInquiries();
    fetchFeeRates();
    fetchSupportTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for support ticket messages
  useEffect(() => {
    if (selectedSupportTicket) {
      fetchSupportMessages(selectedSupportTicket.id);
      supportPollRef.current = setInterval(() => fetchSupportMessages(selectedSupportTicket.id), 5000);
    }
    return () => { if (supportPollRef.current) clearInterval(supportPollRef.current); };
  }, [selectedSupportTicket]);

  // Handle conversation query parameter (from "Message Provider" button)
  useEffect(() => {
    if (conversationId && !loading) {
      fetchInquiryDetail(conversationId);
    }
  }, [conversationId, loading]);

  useEffect(() => {
    if (inquiryId) {
      fetchInquiryDetail(inquiryId);
    }
  }, [inquiryId]);

  useEffect(() => {
    if (prependingRef.current) { prependingRef.current = false; return; }
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      scrollMessagesToBottom(false);
    } else {
      scrollMessagesToBottom(true);
    }
  }, [messages]);

  useEffect(() => {
    if (selectedInquiry) {
      fetchDetailsForInquiry(selectedInquiry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInquiry]);

  // Scroll ONLY the messages column (not the whole page) to the latest message.
  const scrollMessagesToBottom = (smooth = true) => {
    const el = scrollContainerRef.current || supportScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  const scrollToBottom = () => {
    scrollMessagesToBottom(true);
  };

  const jumpToLatest = () => {
    scrollMessagesToBottom(true);
    setShowJumpToLatest(false);
  };

  const handleMessagesScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowJumpToLatest(distanceFromBottom > 200);
  };

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/inquiries`);
      setInquiries(response.data);

      // Check online status of all conversation partners
      const partnerIds = response.data.map(inq => {
        const isProvider = inq.provider_id === user?.id;
        return isProvider ? inq.client_id : inq.provider_id;
      }).filter(Boolean);
      if (partnerIds.length > 0) checkOnlineStatus([...new Set(partnerIds)]);
      
      // Priority: conversationId from URL > clientParam > inquiryId from route > first inquiry
      if (conversationId) {
        fetchInquiryDetail(conversationId);
      } else if (clientParam && response.data.length > 0) {
        // Find inquiry with this client
        const match = response.data.find(inq => 
          inq.client_id === clientParam || inq.provider_id === clientParam
        );
        if (match) {
          fetchInquiryDetail(match.id);
        } else if (response.data.length > 0) {
          fetchInquiryDetail(response.data[0].id);
        }
      } else if (!inquiryId && response.data.length > 0 && window.innerWidth >= 1024) {
        // Desktop only: auto-open the first conversation in the 3-column view.
        // On mobile we show the inbox list first (thread slides in on tap).
        fetchInquiryDetail(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportTickets = async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`);
      setSupportTickets(res.data);
    } catch {}
  };

  const fetchSupportMessages = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/support/tickets/${ticketId}/messages`);
      setSupportMessages(res.data);
      initialLoadRef.current = false;
      setTimeout(() => scrollMessagesToBottom(false), 60);
      // Admin replies are marked read on the server when the owner opens the
      // thread — refresh the nav badge so the Messages red dot clears.
      window.dispatchEvent(new CustomEvent('refreshBadgeCounts'));
    } catch {}
  };

  const handleSendSupportMessage = async (e) => {
    e.preventDefault();
    if (!newSupportMessage.trim() || !selectedSupportTicket) return;
    setSupportSending(true);
    try {
      await axios.post(`${API}/support/tickets/${selectedSupportTicket.id}/messages`, { message: newSupportMessage });
      setNewSupportMessage('');
      fetchSupportMessages(selectedSupportTicket.id);
    } catch {}
    setSupportSending(false);
  };

  const selectSupportTicket = (ticket) => {
    setSelectedSupportTicket(ticket);
    setSelectedInquiry(null);
    setMessages([]);
    setOtherUserDetails(null);
    fetchSupportMessages(ticket.id);
  };

  const fetchInquiryDetail = async (id) => {
    try {
      const response = await axios.get(`${API}/inquiries/${id}?paginated=true&msg_limit=50`);
      initialLoadRef.current = true;
      setSelectedInquiry(response.data.inquiry);
      setMessages(response.data.messages);
      setHasMoreMessages(!!response.data.messages_has_more);
      setOldestCursor(response.data.oldest_cursor || null);
      setSelectedSupportTicket(null);
      setSupportMessages([]);
      // Mark messages as read
      axios.put(`${API}/inquiries/${id}/read`).catch(() => {});
      setTypingUser(null);
    } catch (error) {
      console.error('Error fetching inquiry detail:', error);
    }
  };

  const loadOlderMessages = async () => {
    if (!selectedInquiry || !oldestCursor || loadingOlder) return;
    setLoadingOlder(true);
    prependingRef.current = true;
    try {
      const response = await axios.get(
        `${API}/inquiries/${selectedInquiry.id}?paginated=true&msg_limit=50&before=${encodeURIComponent(oldestCursor)}`
      );
      const older = response.data.messages || [];
      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.id));
        const merged = older.filter((m) => !existing.has(m.id));
        return [...merged, ...prev];
      });
      setHasMoreMessages(!!response.data.messages_has_more);
      setOldestCursor(response.data.oldest_cursor || null);
    } catch (error) {
      console.error('Error loading older messages:', error);
      prependingRef.current = false;
    } finally {
      setLoadingOlder(false);
    }
  };

  const fetchDetailsForInquiry = async (inquiry) => {
    setLoadingDetails(true);
    try {
      // Use the same logic as getOtherParty for consistency
      const otherPartyInfo = getOtherParty(inquiry);
      const otherUserId = otherPartyInfo.id;
      const isCurrentUserProvider = user?.id === inquiry.provider_id;

      // Fetch other user's details
      const userResponse = await axios.get(`${API}/users/${otherUserId}`);
      setOtherUserDetails(userResponse.data);

      // Dedicated, indexed check: do the two parties share a completed paid transaction?
      try {
        const relRes = await axios.get(`${API}/users/${otherUserId}/relationship`);
        setHasTransaction(!!relRes.data.has_paid_transaction);
      } catch {
        setHasTransaction(false);
      }

      // Fetch reviews for the other user (if they're a provider - meaning current user is client)
      if (!isCurrentUserProvider) {
        try {
          const reviewsResponse = await axios.get(`${API}/providers/${otherUserId}/reviews`);
          const reviews = reviewsResponse.data;
          if (reviews.length > 0) {
            // Sort by date and get most recent
            reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setLatestReview(reviews[0]);
          } else {
            setLatestReview(null);
          }
        } catch {
          setLatestReview(null);
        }
      } else {
        setLatestReview(null);
      }

      // Fetch related service if available
      if (inquiry.service_id) {
        try {
          const serviceResponse = await axios.get(`${API}/services/${inquiry.service_id}`);
          setRelatedService(serviceResponse.data);
        } catch {
          setRelatedService(null);
        }
      } else {
        setRelatedService(null);
      }

      // Try to find related booking
      try {
        const bookingsResponse = await axios.get(`${API}/bookings`);
        const allBookings = bookingsResponse.data;
        const relatedBookings = allBookings.filter(b => 
          (b.service_id === inquiry.service_id && 
           ((isCurrentUserProvider && b.provider_id === user?.id) || (!isCurrentUserProvider && b.client_id === user?.id)))
        );
        if (relatedBookings.length > 0) {
          setRelatedBooking(relatedBookings[0]);
        } else {
          setRelatedBooking(null);
        }
      } catch {
        setRelatedBooking(null);
      }

      // Fetch job bid details if this is a job inquiry
      if (inquiry.inquiry_type === 'job_bid' && inquiry.bid_id) {
        try {
          // Get the job details
          if (inquiry.job_id) {
            const jobResponse = await axios.get(`${API}/jobs/${inquiry.job_id}`);
            setRelatedJob(jobResponse.data);
          }
          // Get all bids for this job to find the specific bid
          if (inquiry.job_id) {
            const bidsResponse = await axios.get(`${API}/jobs/${inquiry.job_id}/bids`);
            const bid = bidsResponse.data.find(b => b.id === inquiry.bid_id);
            if (bid) {
              setRelatedJobBid(bid);
            } else {
              setRelatedJobBid(null);
            }
          }
          // Fetch the Open-Time service agreement (if any) for this bid
          try {
            const agreementRes = await axios.get(`${API}/bids/${inquiry.bid_id}/agreement`);
            setAgreement(agreementRes.data.agreement || null);
          } catch {
            setAgreement(null);
          }
        } catch (error) {
          console.error('Error fetching job/bid details:', error);
          setRelatedJob(null);
          setRelatedJobBid(null);
          setAgreement(null);
        }
      } else {
        setRelatedJob(null);
        setRelatedJobBid(null);
        setAgreement(null);
      }

    } catch (error) {
      console.error('Error fetching details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedInquiry) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/inquiries/${selectedInquiry.id}/message`, { message: newMessage });
      
      // Check if message was blocked due to violation
      if (response.data.violation) {
        setViolationWarning({
          count: response.data.violation_count,
          message: response.data.warning_message
        });
        setShowViolationModal(true);
        setNewMessage(''); // Clear the message that contained the violation
        return;
      }
      
      setNewMessage('');
      await fetchInquiryDetail(selectedInquiry.id);
      await fetchInquiries();
    } catch (error) {
      console.error('Error sending reply:', error);
      // Check if account was suspended
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        if (detail?.type === 'account_suspended') {
          setViolationWarning({
            count: detail.violation_count,
            message: detail.message,
            suspended: true
          });
          setShowViolationModal(true);
          return;
        }
      }
      alert(error.response?.data?.detail || t('inquiries.details.failedSendMessage'));
    } finally {
      setSending(false);
    }
  };

  const handleAcceptBooking = async () => {
    if (!relatedBooking) return;
    setProcessingAction(true);
    try {
      const amount = relatedBooking.price || relatedBooking.final_amount || 0;
      const params = new URLSearchParams({ final_amount: amount });
      await axios.put(`${API}/bookings/${relatedBooking.id}/accept-with-quote?${params.toString()}`);
      alert('Booking accepted successfully!');
      fetchDetailsForInquiry(selectedInquiry);
    } catch (error) {
      alert(error.response?.data?.detail || t('inquiries.details.failedAccept'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDeclineBooking = async () => {
    if (!relatedBooking) return;
    if (!window.confirm('Are you sure you want to decline this booking?')) return;
    
    setProcessingAction(true);
    try {
      await axios.put(`${API}/bookings/${relatedBooking.id}`, { status: 'cancelled' });
      alert('Booking declined.');
      fetchDetailsForInquiry(selectedInquiry);
    } catch (error) {
      alert(error.response?.data?.detail || t('inquiries.details.failedDecline'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleModifySubmit = async () => {
    if (!relatedBooking) return;
    setProcessingAction(true);
    try {
      const updateData = {};
      if (modifyData.proposed_date) updateData.scheduled_date = modifyData.proposed_date;
      if (modifyData.proposed_time) updateData.scheduled_time = modifyData.proposed_time;
      if (modifyData.notes) updateData.notes = modifyData.notes;
      
      await axios.put(`${API}/bookings/${relatedBooking.id}`, updateData);
      
      // Also send a message about the modification
      const modificationMsg = `I'd like to propose some changes:\n${modifyData.proposed_amount ? `- Amount: ₱${modifyData.proposed_amount}\n` : ''}${modifyData.proposed_date ? `- Date: ${modifyData.proposed_date}\n` : ''}${modifyData.proposed_time ? `- Time: ${modifyData.proposed_time}\n` : ''}${modifyData.notes ? `- Notes: ${modifyData.notes}` : ''}`;
      
      await axios.post(`${API}/inquiries/${selectedInquiry.id}/message`, { message: modificationMsg });
      
      setShowModifyModal(false);
      setModifyData({ proposed_amount: '', proposed_date: '', proposed_time: '', notes: '' });
      fetchDetailsForInquiry(selectedInquiry);
      fetchInquiryDetail(selectedInquiry.id);
      alert('Modification request sent!');
    } catch (error) {
      alert(error.response?.data?.detail || t('inquiries.details.failedModify'));
    } finally {
      setProcessingAction(false);
    }
  };

  // ==================== Open-Time Service Agreement handlers ====================
  const openAgreementForm = () => {
    setAgreementForm({
      start_date: agreement?.start_date || '',
      start_time: agreement?.start_time || '',
      end_time: agreement?.end_time || '',
      agreed_amount: agreement?.agreed_amount || relatedJobBid?.bid_amount || '',
    });
    setShowAgreementForm(true);
  };

  const handleProposeAgreement = async () => {
    if (!selectedInquiry?.bid_id) return;
    const { start_date, start_time, end_time, agreed_amount } = agreementForm;
    if (!start_date || !start_time || !end_time) {
      alert('Please set the start date, start time and end time.');
      return;
    }
    if (end_time <= start_time) {
      alert('End time must be later than the start time.');
      return;
    }
    if (!agreed_amount || parseFloat(agreed_amount) <= 0) {
      alert('Please enter a valid agreed amount.');
      return;
    }
    setSavingAgreement(true);
    try {
      await axios.post(`${API}/bids/${selectedInquiry.bid_id}/agreement`, {
        start_date, start_time, end_time, agreed_amount: parseFloat(agreed_amount),
      });
      setShowAgreementForm(false);
      await fetchDetailsForInquiry(selectedInquiry);
      await fetchInquiryDetail(selectedInquiry.id);
    } catch (error) {
      alert(error.response?.data?.detail || t('inquiries.details.failedAgreement'));
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleConfirmAgreement = async () => {
    if (!selectedInquiry?.bid_id) return;
    setSavingAgreement(true);
    try {
      await axios.put(`${API}/bids/${selectedInquiry.bid_id}/agreement/confirm`);
      await fetchDetailsForInquiry(selectedInquiry);
      await fetchInquiryDetail(selectedInquiry.id);
    } catch (error) {
      alert(error.response?.data?.detail || t('inquiries.details.failedConfirmAgreement'));
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleDeclineAgreement = async () => {
    if (!selectedInquiry?.bid_id) return;
    if (!window.confirm(t('inquiries.details.confirmDeclineAgreement'))) return;
    setSavingAgreement(true);
    try {
      await axios.put(`${API}/bids/${selectedInquiry.bid_id}/agreement/decline`);
      await fetchDetailsForInquiry(selectedInquiry);
      await fetchInquiryDetail(selectedInquiry.id);
    } catch (error) {
      alert(error.response?.data?.detail || t('inquiries.details.failedDeclineAgreement'));
    } finally {
      setSavingAgreement(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage || !selectedInquiry) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);
      formData.append('caption', newMessage);

      const response = await axios.post(
        `${API}/inquiries/${selectedInquiry.id}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      // Check if upload was blocked due to violation in caption
      if (response.data.violation) {
        setViolationWarning({
          count: response.data.violation_count,
          message: response.data.warning_message
        });
        setShowViolationModal(true);
        setNewMessage('');
        handleClearImage();
        return;
      }

      // Success - clear and refresh
      setNewMessage('');
      handleClearImage();
      await fetchInquiryDetail(selectedInquiry.id);
      await fetchInquiries();
      window.dispatchEvent(new CustomEvent('refreshBadgeCounts'));
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        if (detail?.type === 'account_suspended') {
          setViolationWarning({
            count: detail.violation_count,
            message: detail.message,
            suspended: true
          });
          setShowViolationModal(true);
          return;
        }
      }
      alert(error.response?.data?.detail || t('inquiries.details.failedUploadImage'));
    } finally {
      setUploadingImage(false);
    }
  };

  // Delete conversation handler
  const handleDeleteConversation = async () => {
    if (!selectedInquiry) return;
    
    setDeletingConversation(true);
    try {
      await axios.delete(`${API}/inquiries/${selectedInquiry.id}`);
      setShowDeleteModal(false);
      setSelectedInquiry(null);
      setMessages([]);
      setOtherUserDetails(null);
      await fetchInquiries();
      navigate('/inquiries');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert(error.response?.data?.detail || t('inquiries.details.failedDeleteConversation'));
    } finally {
      setDeletingConversation(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    return date.toLocaleDateString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getOtherParty = (inquiry) => {
    if (!inquiry) return {};
    const isProvider = user?.id === inquiry.provider_id;
    const name = isProvider ? inquiry.client_name : inquiry.provider_name;
    const id = isProvider ? inquiry.client_id : inquiry.provider_id;
    
    // If the stored name matches the current user's name (data bug), 
    // show "Client" or "Provider" as fallback
    const displayName = (name === user?.name) 
      ? (isProvider ? 'Client' : inquiry.service_title)
      : name;
    
    return {
      id: id,
      name: displayName,
      role: isProvider ? 'Client' : 'Provider'
    };
  };

  const calculatePaymentBreakdown = (amount) => {
    const baseAmount = parseFloat(amount) || 0;
    const platformFee = baseAmount * (feeRates.client_fee_rate / 100);
    const totalForClient = baseAmount + platformFee;
    return {
      baseAmount: baseAmount.toFixed(2),
      platformFee: platformFee.toFixed(2),
      total: totalForClient.toFixed(2),
      feeRate: feeRates.client_fee_rate
    };
  };

  const calculateEarningsBreakdown = (amount) => {
    const baseAmount = parseFloat(amount) || 0;
    const platformDeduction = baseAmount * (feeRates.provider_fee_rate / 100);
    const netEarnings = baseAmount - platformDeduction;
    return {
      baseAmount: baseAmount.toFixed(2),
      platformDeduction: platformDeduction.toFixed(2),
      netEarnings: netEarnings.toFixed(2),
      feeRate: feeRates.provider_fee_rate
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <div className="text-xl font-semibold text-gray-600">Loading inquiries...</div>
        </div>
        <Footer />
      </div>
    );
  }

  const otherParty = selectedInquiry ? getOtherParty(selectedInquiry) : {};
  const isProvider = user?.id === selectedInquiry?.provider_id;
  // For job bids, use bid amount; for services, use service/booking price
  const serviceAmount = relatedJobBid?.bid_amount || relatedService?.price || relatedBooking?.price || 0;
  const isJobInquiry = selectedInquiry?.inquiry_type === 'job_bid';

  // Mobile master-detail: when a thread (or support ticket) is open, the chat
  // panel slides in from the right over the inbox list (Airbnb-style).
  const detailOpen = !!(selectedInquiry || selectedSupportTicket);
  const closeThread = () => {
    setSelectedInquiry(null);
    setSelectedSupportTicket(null);
    navigate('/inquiries');
  };

  // Friendly day label for date separators inside a thread.
  const dayLabel = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    if (sameDay(d, today)) return 'Today';
    if (sameDay(d, yest)) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="inquiries-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-6">
          {t('inquiries.title')}
        </h1>

        {inquiries.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">💬</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('inquiries.noInquiriesTitle')}</h2>
            <p className="text-gray-600 mb-6">
              {effectiveRole === 'provider' 
                ? t('inquiries.noInquiriesProvider')
                : t('inquiries.noInquiriesClient')}
            </p>
            <button
              onClick={() => navigate(effectiveRole === 'provider' ? '/jobs' : '/services')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold"
              data-testid="inquiries-empty-cta"
            >
              {effectiveRole === 'provider' ? t('inquiries.browseJobs') : t('inquiries.browseServices')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Column 1: Inquiries List */}
            <div className={`${detailOpen ? 'hidden' : 'block'} lg:block lg:col-span-3 bg-white rounded-lg shadow-md overflow-hidden`}>
              <div className="p-4 border-b bg-gray-50">
                <h2 className="font-semibold text-gray-800">{t('inquiries.conversations')}</h2>
              </div>
              <div className="divide-y max-h-[700px] overflow-y-auto">
                {inquiries.map((inquiry) => {
                  const party = getOtherParty(inquiry);
                  const isSelected = selectedInquiry?.id === inquiry.id;
                  const hasUnread = inquiry.unread_count > 0;
                  
                  return (
                    <div
                      key={inquiry.id}
                      onClick={async () => {
                        await fetchInquiryDetail(inquiry.id);
                        navigate(`/inquiries/${inquiry.id}`);
                        // Update local unread count immediately for this inquiry
                        setInquiries(prev => prev.map(inq => 
                          inq.id === inquiry.id ? { ...inq, unread_count: 0 } : inq
                        ));
                        // Emit event for navbar to refresh badge counts
                        window.dispatchEvent(new CustomEvent('refreshBadgeCounts'));
                      }}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                        isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      } ${hasUnread && !isSelected ? 'bg-orange-50 border-l-4 border-orange-400' : ''} ${inquiry.disabled && !isSelected ? 'opacity-60' : ''}`}
                      data-testid={`inquiry-item-${inquiry.id}`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <h3 className={`truncate flex-1 flex items-center gap-1.5 ${hasUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
                          {inquiry.disabled && (
                            <Ban className="w-3.5 h-3.5 inline-block text-red-400 mr-1 -mt-0.5" />
                          )}
                          {isUserOnline(party.id) && (
                            <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" title="Online"></span>
                          )}
                          <span className="truncate">{party.name}</span>
                          {hasUnread && (
                            <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full flex-shrink-0">
                              {inquiry.unread_count > 9 ? '9+' : inquiry.unread_count}
                            </span>
                          )}
                        </h3>
                        <span className="text-xs text-gray-500 ml-2">
                          {formatTime(inquiry.last_message_at || inquiry.created_at)}
                        </span>
                      </div>
                      <p className={`text-sm truncate ${hasUnread ? 'text-blue-700 font-medium' : 'text-blue-600'}`}>{inquiry.service_title}</p>
                      <p className={`text-sm truncate mt-1 ${hasUnread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                        {inquiry.last_message || t('inquiries.noMessages')}
                      </p>
                    </div>
                  );
                })}

                {/* Admin Support Section */}
                {supportTickets.length > 0 && (
                  <>
                    <div
                      className="p-3 border-t border-b bg-orange-50 flex items-center justify-between cursor-pointer hover:bg-orange-100 transition"
                      onClick={() => setSupportExpanded(!supportExpanded)}
                      data-testid="support-section-toggle"
                    >
                      <span className="text-xs font-semibold text-orange-600 uppercase tracking-wider flex items-center gap-2">
                        Admin Support
                        {supportTickets.filter(t => t.status === 'open').length > 0 && (
                          <span className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                            {supportTickets.filter(t => t.status === 'open').length}
                          </span>
                        )}
                      </span>
                      <svg className={`w-4 h-4 text-orange-400 transition-transform ${supportExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {supportExpanded && (
                      <>
                        {/* Open tickets */}
                        {supportTickets.filter(t => t.status === 'open').map((ticket) => (
                          <div
                            key={`support-${ticket.id}`}
                            onClick={() => selectSupportTicket(ticket)}
                            className={`p-4 cursor-pointer hover:bg-orange-50 transition border-l-4 ${
                              selectedSupportTicket?.id === ticket.id ? 'bg-orange-50 border-l-orange-500' : 'border-l-orange-200'
                            }`}
                            data-testid={`support-inquiry-${ticket.id}`}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <h3 className="font-semibold text-orange-700 truncate flex-1">Admin Support</h3>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full ml-2 bg-green-100 text-green-700">open</span>
                            </div>
                            <p className="text-sm text-orange-500 truncate">{ticket.subject}</p>
                            <p className="text-xs text-gray-400 mt-1">#{ticket.id?.slice(0, 8).toUpperCase()}</p>
                          </div>
                        ))}

                        {/* Closed tickets - collapsible history */}
                        {supportTickets.filter(t => t.status === 'closed').length > 0 && (
                          <>
                            <div
                              className="px-4 py-2 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition"
                              onClick={() => setShowClosedTickets(!showClosedTickets)}
                              data-testid="closed-tickets-toggle"
                            >
                              <span className="text-[10px] font-medium text-gray-500 uppercase">
                                History ({supportTickets.filter(t => t.status === 'closed').length})
                              </span>
                              <svg className={`w-3 h-3 text-gray-400 transition-transform ${showClosedTickets ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                            {showClosedTickets && supportTickets.filter(t => t.status === 'closed').map((ticket) => (
                              <div
                                key={`support-closed-${ticket.id}`}
                                onClick={() => selectSupportTicket(ticket)}
                                className={`p-4 cursor-pointer hover:bg-gray-50 transition border-l-4 opacity-60 ${
                                  selectedSupportTicket?.id === ticket.id ? 'bg-gray-100 border-l-gray-400' : 'border-l-gray-200'
                                }`}
                                data-testid={`support-inquiry-closed-${ticket.id}`}
                              >
                                <div className="flex justify-between items-start mb-1">
                                  <h3 className="font-semibold text-gray-500 truncate flex-1">Admin Support</h3>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full ml-2 bg-gray-200 text-gray-500">closed</span>
                                </div>
                                <p className="text-sm text-gray-400 truncate">{ticket.subject}</p>
                                <p className="text-xs text-gray-400 mt-1">#{ticket.id?.slice(0, 8).toUpperCase()}</p>
                              </div>
                            ))}
                          </>
                        )}

                        {supportTickets.filter(t => t.status === 'open').length === 0 && !showClosedTickets && (
                          <div className="px-4 py-3 text-center text-xs text-gray-400">No open tickets</div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Mobile slide-in panel (Airbnb-style): wraps the chat + details.
                On desktop (lg) it uses `contents` so the two columns sit back in
                the 12-col grid; on mobile it becomes a full-screen panel that
                slides in from the right when a thread is open. */}
            <div
              className={`lg:contents lg:pointer-events-auto fixed inset-0 z-50 bg-gray-50 overflow-y-auto transition-transform duration-300 ease-out ${detailOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
              data-testid="thread-panel"
            >
            {/* Column 2: Chat Area */}
            <div className="lg:col-span-5 bg-white lg:rounded-lg shadow-md flex flex-col min-h-screen lg:min-h-0 lg:h-[700px]">
              {/* Mobile-only back bar (return to inbox) */}
              {detailOpen && (
                <button
                  type="button"
                  onClick={closeThread}
                  className="lg:hidden flex items-center gap-1.5 px-4 pb-3 text-sm font-semibold text-gray-700 border-b bg-white sticky top-0 z-10 active:bg-gray-50"
                  style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
                  data-testid="thread-back-btn"
                  aria-label="Back to inbox"
                >
                  <ArrowLeft className="w-5 h-5" /> Back to inbox
                </button>
              )}
              {/* Support Ticket Chat */}
              {selectedSupportTicket ? (
                <>
                  <div className="p-4 border-b bg-orange-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        </div>
                        <div>
                          <h2 className="font-semibold text-orange-800">Admin Support</h2>
                          <p className="text-sm text-orange-600">{selectedSupportTicket.subject}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${selectedSupportTicket.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {selectedSupportTicket.status}
                      </span>
                    </div>
                  </div>

                  <div ref={supportScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-orange-50/20" data-testid="support-messages-area">
                    {supportMessages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.sender_role === 'admin' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                          msg.sender_role === 'admin'
                            ? 'bg-orange-100 text-gray-800 rounded-bl-none'
                            : 'bg-blue-500 text-white rounded-br-none'
                        }`}>
                          {msg.sender_role === 'admin' && (
                            <p className="text-[10px] font-bold text-orange-600 mb-0.5">{msg.sender_name} (Admin)</p>
                          )}
                          <p className="text-sm">{msg.message}</p>
                          <p className={`text-[10px] mt-1 ${msg.sender_role === 'admin' ? 'text-gray-400' : 'text-blue-100'}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {selectedSupportTicket.status === 'open' ? (
                    <form onSubmit={handleSendSupportMessage} className="p-4 border-t border-orange-200 bg-orange-50/50">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newSupportMessage}
                          onChange={(e) => setNewSupportMessage(e.target.value)}
                          placeholder={t('inquiries.replySupport')}
                          className="flex-1 px-4 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                          data-testid="support-reply-input"
                        />
                        <button
                          type="submit"
                          disabled={supportSending || !newSupportMessage.trim()}
                          className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 font-semibold disabled:opacity-50"
                          data-testid="support-reply-send"
                        >
                          {t('inquiries.send')}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="p-4 border-t bg-gray-50 text-center text-sm text-gray-400">
                      {t('inquiries.ticketClosed')}
                    </div>
                  )}
                </>
              ) : selectedInquiry ? (
                <>
                  {/* Chat Header */}
                  <div className={`p-4 border-b ${selectedInquiry.disabled ? 'bg-red-50' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className={`font-semibold flex items-center gap-2 ${selectedInquiry.disabled ? 'text-gray-500' : 'text-gray-800'}`}>
                          {selectedInquiry.disabled && selectedInquiry.disabled_reason === 'user_deleted'
                            ? (otherParty.name || 'Deleted User')
                            : (
                              <Link
                                to={`/user/${otherUserDetails?.id || otherParty.id}`}
                                className="hover:text-blue-600 hover:underline transition"
                                data-testid="chat-header-name-link"
                              >
                                {otherUserDetails?.name || otherParty.name}
                              </Link>
                            )}
                          {isUserOnline(otherParty.id) ? (
                            <span className="inline-flex items-center gap-1 text-xs font-normal text-green-500" data-testid="chat-online-status">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              Online
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-normal text-gray-400" data-testid="chat-offline-status">
                              <span className="w-2 h-2 bg-gray-300 rounded-full"></span>
                              Offline
                            </span>
                          )}
                        </h2>
                        <p className="text-sm text-blue-600 flex items-center gap-2">
                          {selectedInquiry.service_title}
                          {wsConnected && (
                            <span className="inline-flex items-center gap-1 text-xs text-green-500">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                              Live
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedInquiry.disabled ? (
                          <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700" data-testid="thread-disabled-badge">
                            Disabled
                          </span>
                        ) : (
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            selectedInquiry.status === 'open' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {selectedInquiry.status}
                          </span>
                        )}
                        <button
                          onClick={() => setShowDeleteModal(true)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Delete conversation"
                          data-testid="delete-conversation-btn"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    {/* Disabled thread banner */}
                    {selectedInquiry.disabled && (
                      <div className="mt-3 flex items-center gap-2 bg-red-100 border border-red-200 rounded-lg px-3 py-2" data-testid="thread-disabled-banner">
                        <Ban className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700">
                          {selectedInquiry.disabled_reason === 'job_cancelled'
                            ? 'This conversation has been disabled because the job was cancelled by the client.'
                            : selectedInquiry.disabled_reason === 'user_deleted'
                            ? 'This conversation has been disabled because the other user no longer exists.'
                            : selectedInquiry.disabled_reason === 'job_deleted'
                            ? 'This conversation has been disabled because the job no longer exists.'
                            : 'This conversation has been disabled.'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Messages */}
                  <div className="relative flex-1 flex flex-col min-h-0">
                  <div ref={scrollContainerRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="thread-messages-scroll">
                    {hasMoreMessages && (
                      <div className="text-center pb-2">
                        <button
                          onClick={loadOlderMessages}
                          disabled={loadingOlder}
                          className="text-xs text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50"
                          data-testid="load-older-messages"
                        >
                          {loadingOlder ? t('inquiries.loading') : t('inquiries.loadOlder')}
                        </button>
                      </div>
                    )}
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        {t('inquiries.noMessagesStart')}
                      </div>
                    ) : (
                      messages.map((msg, idx) => {
                        const isOwnMessage = msg.sender_id === user?.id;
                        const prev = messages[idx - 1];
                        const showDivider = idx === 0 || (prev && new Date(prev.created_at).toDateString() !== new Date(msg.created_at).toDateString());
                        return (
                          <React.Fragment key={msg.id}>
                            {showDivider && (
                              <div className="flex justify-center my-2" data-testid="thread-date-divider">
                                <span className="px-3 py-1 text-[11px] font-medium text-gray-500 bg-gray-100 rounded-full">
                                  {dayLabel(msg.created_at)}
                                </span>
                              </div>
                            )}
                          <div
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg p-3 ${
                                isOwnMessage
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              <p className="text-sm font-semibold mb-1">
                                {isOwnMessage ? 'You' : msg.sender_name}
                              </p>
                              {/* Show image if message has image_url */}
                              {msg.image_url && (
                                <div className="mb-2">
                                  <img 
                                    src={msg.image_url.startsWith('/') ? `${BACKEND_URL}${msg.image_url}` : msg.image_url}
                                    alt="Shared image"
                                    className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition"
                                    onClick={() => setViewerImage(msg.image_url.startsWith('/') ? `${BACKEND_URL}${msg.image_url}` : msg.image_url)}
                                    style={{ maxHeight: '200px', objectFit: 'contain' }}
                                    data-testid="message-image-thumb"
                                  />
                                </div>
                              )}
                              {/* Show text message (hide if it's just the default image text) */}
                              {msg.message && msg.message !== '📷 Image' && (
                                <p className="whitespace-pre-wrap">{msg.message}</p>
                              )}
                              <p className={`text-xs mt-1 flex items-center gap-1 ${
                                isOwnMessage ? 'text-blue-200' : 'text-gray-500'
                              }`}>
                                {formatTime(msg.created_at)}
                                {isOwnMessage && (
                                  <span title={msg.read ? 'Seen' : 'Sent'}>
                                    {msg.read ? (
                                      <svg className="w-3.5 h-3.5 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    ) : (
                                      <svg className="w-3.5 h-3.5 text-blue-300/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                    )}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          </React.Fragment>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  {showJumpToLatest && (
                    <button
                      type="button"
                      onClick={jumpToLatest}
                      className="absolute bottom-4 right-4 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-2 shadow-lg transition active:scale-95"
                      data-testid="jump-to-latest"
                      aria-label="Jump to latest messages"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                    </button>
                  )}
                  </div>

                  {/* Typing Indicator */}
                  {typingUser && (
                    <div className="px-4 pb-1">
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <span className="flex gap-0.5">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </span>
                        <span>{typingUser} {t('inquiries.isTyping')}</span>
                      </div>
                    </div>
                  )}

                  {/* Message Input */}
                  {selectedInquiry.disabled ? (
                    <div className="p-4 border-t bg-gray-100" data-testid="thread-disabled-input">
                      <div className="flex items-center justify-center gap-2 text-gray-500 py-2">
                        <Ban className="w-5 h-5" />
                        <span className="text-sm font-medium">
                          {selectedInquiry.disabled_reason === 'job_cancelled'
                            ? 'Messaging is disabled — the job has been cancelled.'
                            : selectedInquiry.disabled_reason === 'user_deleted'
                            ? 'Messaging is disabled — the other user no longer exists.'
                            : t('inquiries.messagingDisabled')}
                        </span>
                      </div>
                    </div>
                  ) : (
                  <div className="p-4 border-t">
                    {/* Image Preview */}
                    {imagePreview && (
                      <div className="mb-3 relative inline-block">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-24 rounded-lg border"
                        />
                        <button
                          onClick={handleClearImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          type="button"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    <form onSubmit={selectedImage ? (e) => { e.preventDefault(); handleImageUpload(); } : handleSendReply} className="flex gap-2">
                      {/* Hidden file input */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageSelect}
                        accept="image/*"
                        className="hidden"
                        data-testid="image-upload-input"
                      />
                      
                      {/* Image upload button */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="Upload image"
                        disabled={sending || uploadingImage}
                        data-testid="image-upload-btn"
                      >
                        <Image className="w-6 h-6" />
                      </button>
                      
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => { setNewMessage(e.target.value); emitTyping(); }}
                        placeholder={selectedImage ? t('inquiries.addCaption') : t('inquiries.typeMessage')}
                        className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={sending || uploadingImage}
                        data-testid="inquiry-message-input"
                      />
                      <button
                        type="submit"
                        disabled={(sending || uploadingImage) || (!selectedImage && !newMessage.trim())}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                        data-testid="send-reply-btn"
                      >
                        {sending || uploadingImage ? '...' : selectedImage ? t('inquiries.upload') : t('inquiries.send')}
                      </button>
                    </form>
                  </div>
                  )}
                </>
              ) : !selectedSupportTicket ? (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  {t('inquiries.selectConversation')}
                </div>
              ) : null}
            </div>

            {/* Column 3: Details Panel */}
            <div className="lg:col-span-4 bg-white lg:rounded-lg shadow-md overflow-hidden lg:h-[700px]" style={{ minHeight: '300px' }}>
              {selectedInquiry ? (
                <div className="h-full overflow-y-auto">
                  {loadingDetails ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-gray-500">{t('inquiries.loadingDetails')}</div>
                    </div>
                  ) : (
                    <div className="p-4 space-y-6">
                      {/* Disabled Thread Alert Banner */}
                      {selectedInquiry.disabled && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="details-disabled-banner">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <h3 className="text-sm font-bold text-red-800">
                                {selectedInquiry.disabled_reason === 'job_cancelled'
                                  ? t('inquiries.details.disJobCancelledTitle')
                                  : selectedInquiry.disabled_reason === 'user_deleted'
                                  ? t('inquiries.details.disUserDeletedTitle')
                                  : selectedInquiry.disabled_reason === 'job_deleted'
                                  ? t('inquiries.details.disJobDeletedTitle')
                                  : t('inquiries.details.disDefaultTitle')}
                              </h3>
                              <p className="text-sm text-red-600 mt-1">
                                {selectedInquiry.disabled_reason === 'job_cancelled'
                                  ? t('inquiries.details.disJobCancelledDesc')
                                  : selectedInquiry.disabled_reason === 'user_deleted'
                                  ? t('inquiries.details.disUserDeletedDesc')
                                  : selectedInquiry.disabled_reason === 'job_deleted'
                                  ? t('inquiries.details.disJobDeletedDesc')
                                  : t('inquiries.details.disDefaultDesc')}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Row 1: Communication Details */}
                      <div className="border-b pb-4">
                        {selectedInquiry.disabled && selectedInquiry.disabled_reason === 'user_deleted' ? (
                          <span className="text-2xl font-bold text-gray-400" data-testid="other-party-name">
                            {otherParty.name || t('inquiries.details.deletedUser')}
                          </span>
                        ) : (
                        <Link 
                          to={`/user/${otherUserDetails?.id || otherParty.id}`}
                          className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition"
                          data-testid="other-party-name"
                        >
                          {otherUserDetails?.name || otherParty.name}
                        </Link>
                        )}
                        {/* Report / Block controls — enabled only when both parties share a paid transaction */}
                        {!(selectedInquiry.disabled && selectedInquiry.disabled_reason === 'user_deleted') && (otherUserDetails?.id || otherParty.id) && (
                          <div className="mt-3" data-testid="thread-moderation-controls">
                            <ReportBlockControls
                              targetUserId={otherUserDetails?.id || otherParty.id}
                              targetName={otherUserDetails?.name || otherParty.name}
                              threadId={selectedInquiry.id}
                              disabled={!hasTransaction}
                              disabledHint={t('inquiries.details.reportBlockDisabledHint')}
                            />
                            {!hasTransaction && (
                              <p className="text-xs text-gray-400 mt-1.5">
                                {t('inquiries.details.reportBlockUnlockNote')}
                              </p>
                            )}
                          </div>
                        )}
                        <div className="mt-3 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                              {relatedService?.title || selectedInquiry.service_title || t('inquiries.details.directMessage')}
                            </span>
                          </div>
                          {/* Show job details if it's a job inquiry */}
                          {isJobInquiry && relatedJob && (
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {t('inquiries.details.jobStatus')} <span className={`font-medium ${
                                  relatedJob.status === 'open' ? 'text-green-600' 
                                  : relatedJob.status === 'in_progress' ? 'text-blue-600' 
                                  : relatedJob.status === 'cancelled' ? 'text-red-600'
                                  : 'text-gray-600'}`}>
                                  {t('bookingsList.statuses.' + relatedJob.status, relatedJob.status?.replace('_', ' '))}
                                </span>
                              </span>
                            </div>
                          )}
                          {/* Show notice when job not found (deleted) */}
                          {isJobInquiry && !relatedJob && selectedInquiry.disabled_reason === 'job_deleted' && (
                            <div className="flex items-center gap-2 text-sm">
                              <XCircle className="w-4 h-4 text-red-400" />
                              <span className="text-red-600 font-medium">{t('inquiries.details.jobNoLongerExists')}</span>
                            </div>
                          )}
                          {/* Job Schedule - Preferred Date & Time */}
                          {isJobInquiry && relatedJob && (relatedJob.preferred_date || relatedJob.preferred_time) && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mt-2">
                              <p className="text-xs font-semibold text-orange-700 mb-1">{t('inquiries.details.clientPreferredSchedule')}</p>
                              <div className="flex flex-wrap gap-3 text-sm">
                                {relatedJob.preferred_date && (
                                  <span className="flex items-center gap-1 text-gray-700">
                                    <Calendar className="w-3 h-3 text-orange-500" />
                                    {formatDate(relatedJob.preferred_date)}
                                  </span>
                                )}
                                {(relatedJob.preferred_start_time || relatedJob.preferred_end_time) && (
                                  <span className="flex items-center gap-1 text-gray-700">
                                    <Clock className="w-3 h-3 text-orange-500" />
                                    {relatedJob.preferred_start_time && relatedJob.preferred_end_time 
                                      ? `${relatedJob.preferred_start_time} - ${relatedJob.preferred_end_time}`
                                      : relatedJob.preferred_start_time || relatedJob.preferred_end_time}
                                  </span>
                                )}
                              </div>
                              {relatedJob.deadline && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {t('inquiries.details.deadline')} {formatDate(relatedJob.deadline)}
                                </p>
                              )}
                            </div>
                          )}
                          {isJobInquiry && relatedJobBid && (
                            <div className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {t('inquiries.details.bidStatus')} <span className={`font-medium ${relatedJobBid.status === 'accepted' ? 'text-green-600' : relatedJobBid.status === 'pending' ? 'text-amber-600' : 'text-red-600'}`}>
                                  {t('bookingsList.statuses.' + relatedJobBid.status, relatedJobBid.status?.charAt(0).toUpperCase() + relatedJobBid.status?.slice(1))}
                                </span>
                              </span>
                            </div>
                          )}
                          {(relatedBooking?.scheduled_date || relatedService) && !isJobInquiry && (
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {relatedBooking?.scheduled_date 
                                  ? formatDate(relatedBooking.scheduled_date)
                                  : t('inquiries.details.dateNotSet')}
                                {relatedBooking?.scheduled_time && ` ${t('inquiries.details.at')} ${relatedBooking.scheduled_time}`}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-sm">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-800 font-semibold">
                              {isJobInquiry ? t('inquiries.details.bidAmount') : ''}
                              ₱{serviceAmount?.toLocaleString() || '0'} 
                              {relatedService?.price_unit && !isJobInquiry && ` / ${relatedService.price_unit}`}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Open-Time Service Agreement */}
                      {isJobInquiry && relatedJob?.job_type === 'open_time' && relatedJobBid?.status === 'accepted' && (
                        <div className="border-b pb-4" data-testid="agreement-section">
                          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('inquiries.details.serviceAgreement')}</h3>

                          {(!agreement || agreement.status === 'declined') ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              {agreement?.status === 'declined' && (
                                <p className="text-sm text-red-600 mb-2 font-medium">{t('inquiries.details.prevDeclined')} {isProvider ? t('inquiries.details.waitingClientNewTerms') : t('inquiries.details.proposeNewTerms')}</p>
                              )}
                              {isProvider ? (
                                <p className="text-sm text-blue-700">{t('inquiries.details.waitingClientPropose')}</p>
                              ) : !showAgreementForm ? (
                                <>
                                  <p className="text-sm text-blue-700 mb-3">{t('inquiries.details.proposeScheduleDesc')}</p>
                                  <button
                                    onClick={openAgreementForm}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                                    data-testid="propose-agreement-btn"
                                  >
                                    {t('inquiries.details.proposeAgreement')}
                                  </button>
                                </>
                              ) : null}
                            </div>
                          ) : agreement.status === 'proposed' ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                              <p className="text-xs font-semibold text-amber-700 uppercase mb-1">{t('inquiries.details.pendingConfirmation')}</p>
                              <div className="flex justify-between text-sm"><span className="text-gray-600">{t('inquiries.details.dateColon')}</span><span className="text-gray-800 font-medium">{formatDate(agreement.start_date)}</span></div>
                              <div className="flex justify-between text-sm"><span className="text-gray-600">{t('inquiries.details.timeColon')}</span><span className="text-gray-800 font-medium">{agreement.start_time} – {agreement.end_time}</span></div>
                              <div className="flex justify-between text-sm"><span className="text-gray-600">{t('inquiries.details.agreedAmount')}</span><span className="text-gray-800 font-medium">₱{Number(agreement.agreed_amount).toLocaleString()}</span></div>
                              {isProvider ? (
                                <div className="flex gap-2 pt-2">
                                  <button onClick={handleConfirmAgreement} disabled={savingAgreement} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium text-sm" data-testid="agreement-confirm-btn">{t('inquiries.details.confirm')}</button>
                                  <button onClick={handleDeclineAgreement} disabled={savingAgreement} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium text-sm" data-testid="agreement-decline-btn">{t('inquiries.details.decline')}</button>
                                </div>
                              ) : !showAgreementForm ? (
                                <div className="pt-2">
                                  <p className="text-sm text-amber-700 mb-2">{t('inquiries.details.waitingProviderConfirm')}</p>
                                  <button onClick={openAgreementForm} className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-testid="agreement-edit-btn">{t('inquiries.details.editTerms')}</button>
                                </div>
                              ) : null}
                            </div>
                          ) : agreement.status === 'confirmed' ? (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-1" data-testid="agreement-confirmed">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-semibold text-green-700">{t('inquiries.details.agreementConfirmed')}</span>
                              </div>
                              <div className="flex justify-between text-sm"><span className="text-gray-600">{t('inquiries.details.dateColon')}</span><span className="text-gray-800 font-medium">{formatDate(agreement.start_date)}</span></div>
                              <div className="flex justify-between text-sm"><span className="text-gray-600">{t('inquiries.details.timeColon')}</span><span className="text-gray-800 font-medium">{agreement.start_time} – {agreement.end_time}</span></div>
                              <div className="flex justify-between text-sm"><span className="text-gray-600">{t('inquiries.details.agreedAmount')}</span><span className="text-gray-800 font-medium">₱{Number(agreement.agreed_amount).toLocaleString()}</span></div>
                              <p className="text-xs text-green-700 pt-1">{isProvider ? t('inquiries.details.clientCanPay') : t('inquiries.details.youCanPay')}</p>
                            </div>
                          ) : null}

                          {/* Inline propose/edit form (client only) */}
                          {!isProvider && showAgreementForm && (
                            <div className="bg-white border border-blue-300 rounded-lg p-3 mt-3 space-y-3" data-testid="agreement-form">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">{t('inquiries.details.startDate')}</label>
                                <input type="date" value={agreementForm.start_date} min={new Date().toISOString().split('T')[0]} onChange={(e) => setAgreementForm(p => ({ ...p, start_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="agreement-start-date" />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t('inquiries.details.startTime')}</label>
                                  <input type="time" value={agreementForm.start_time} onChange={(e) => setAgreementForm(p => ({ ...p, start_time: e.target.value, end_time: (p.end_time && p.end_time <= e.target.value) ? '' : p.end_time }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="agreement-start-time" />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t('inquiries.details.endTime')}</label>
                                  <input type="time" value={agreementForm.end_time} min={agreementForm.start_time || undefined} disabled={!agreementForm.start_time} onChange={(e) => setAgreementForm(p => ({ ...p, end_time: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" data-testid="agreement-end-time" />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">{t('inquiries.details.agreedAmountInput')}</label>
                                <input type="number" min="0" step="0.01" value={agreementForm.agreed_amount} onChange={(e) => setAgreementForm(p => ({ ...p, agreed_amount: e.target.value }))} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="agreement-amount" />
                                <p className="text-xs text-gray-400 mt-1">{t('inquiries.details.agreedAmountHint')}</p>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={handleProposeAgreement} disabled={savingAgreement} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm" data-testid="agreement-submit-btn">{savingAgreement ? t('inquiries.details.sending') : t('inquiries.details.sendToProvider')}</button>
                                <button onClick={() => setShowAgreementForm(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm">{t('inquiries.details.cancel')}</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Row 2: Decision Buttons (if there's a pending booking) */}
                      {relatedBooking && relatedBooking.status === 'pending' && isProvider && (
                        <div className="border-b pb-4">
                          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('inquiries.details.actionRequired')}</h3>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={handleAcceptBooking}
                              disabled={processingAction}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                              data-testid="accept-booking-btn"
                            >
                              <CheckCircle className="w-4 h-4" />
                              {t('inquiries.details.accept')}
                            </button>
                            <button
                              onClick={handleDeclineBooking}
                              disabled={processingAction}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                              data-testid="decline-booking-btn"
                            >
                              <XCircle className="w-4 h-4" />
                              {t('inquiries.details.decline')}
                            </button>
                            <button
                              onClick={() => setShowModifyModal(true)}
                              disabled={processingAction}
                              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 font-medium"
                              data-testid="modify-booking-btn"
                            >
                              <Edit3 className="w-4 h-4" />
                              {t('inquiries.details.modify')}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Row 3: About the Other Person */}
                      {otherUserDetails && (
                        <div className="border-b pb-4">
                          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                            {t('inquiries.details.about', { name: (otherUserDetails?.name || otherParty.name)?.split(' ')[0] })}
                          </h3>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {otherUserDetails.location || otherUserDetails.city || t('inquiries.details.locationNotSpecified')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Star className="w-4 h-4 text-amber-500" />
                              <span className="text-gray-600">
                                {otherUserDetails.rating 
                                  ? t('inquiries.details.ratingSuffix', { rating: otherUserDetails.rating.toFixed(1) })
                                  : t('inquiries.details.noRatings')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              {otherUserDetails.id_verified ? (
                                <>
                                  <ShieldCheck className="w-4 h-4 text-green-500" />
                                  <span className="text-green-600 font-medium">{t('inquiries.details.verified')}</span>
                                </>
                              ) : (
                                <>
                                  <Shield className="w-4 h-4 text-gray-400" />
                                  <span className="text-gray-500">{t('inquiries.details.notVerified')}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {t('inquiries.details.joined', { date: formatDate(otherUserDetails.created_at) })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <UserIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {otherUserDetails.available_roles?.length > 1 
                                  ? t('inquiries.details.providerAndClient')
                                  : otherUserDetails.role === 'provider' 
                                    ? t('inquiries.details.providerOnly')
                                    : t('inquiries.details.clientOnly')}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Row 4: Most Recent Review */}
                      {latestReview && (
                        <div className="border-b pb-4">
                          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('inquiries.details.mostRecentReview')}</h3>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center gap-1 mb-2">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-4 h-4 ${i < latestReview.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} 
                                />
                              ))}
                              <span className="text-sm text-gray-500 ml-2">
                                {formatDate(latestReview.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 italic">
                              "{latestReview.comment || t('inquiries.details.noComment')}"
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              - {latestReview.reviewer_name || latestReview.client_name || t('inquiries.details.anonymous')}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Row 5: Request Details */}
                      {(relatedBooking || relatedService) && (
                        <div className="border-b pb-4">
                          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('inquiries.details.requestDetails')}</h3>
                          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            {relatedBooking && (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">{t('inquiries.details.statusColon')}</span>
                                  <span className={`font-medium ${
                                    relatedBooking.status === 'pending' ? 'text-amber-600' :
                                    relatedBooking.status === 'accepted' ? 'text-green-600' :
                                    relatedBooking.status === 'completed' ? 'text-blue-600' :
                                    'text-gray-600'
                                  }`}>
                                    {t('bookingsList.statuses.' + relatedBooking.status, relatedBooking.status?.charAt(0).toUpperCase() + relatedBooking.status?.slice(1))}
                                  </span>
                                </div>
                                {relatedBooking.message && (
                                  <div className="text-sm">
                                    <span className="text-gray-500">{t('inquiries.details.messageColon')}</span>
                                    <p className="text-gray-700 mt-1">{relatedBooking.message}</p>
                                  </div>
                                )}
                                {relatedBooking.notes && (
                                  <div className="text-sm">
                                    <span className="text-gray-500">{t('inquiries.details.notesColon')}</span>
                                    <p className="text-gray-700 mt-1">{relatedBooking.notes}</p>
                                  </div>
                                )}
                              </>
                            )}
                            {relatedService && (
                              <div className="text-sm">
                                <span className="text-gray-500">{t('inquiries.details.serviceDescription')}</span>
                                <p className="text-gray-700 mt-1 line-clamp-3">{relatedService.description}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Row 6: Payment/Earnings Breakdown */}
                      <div className="pb-4">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                          {isProvider 
                            ? (isJobInquiry ? t('inquiries.details.jobEarnings') : t('inquiries.details.yourEarnings'))
                            : (isJobInquiry ? t('inquiries.details.jobPayment') : t('inquiries.details.paymentBreakdown'))
                          }
                        </h3>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                          {isProvider ? (
                            // Provider view - show earnings after deduction
                            (() => {
                              const breakdown = calculateEarningsBreakdown(serviceAmount);
                              return (
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{isJobInquiry ? t('inquiries.details.bidAmountColon') : t('inquiries.details.serviceAmountColon')}</span>
                                    <span className="text-gray-800">₱{breakdown.baseAmount}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('inquiries.details.platformFee', { rate: breakdown.feeRate })}</span>
                                    <span className="text-red-600">-₱{breakdown.platformDeduction}</span>
                                  </div>
                                  <div className="border-t pt-2 mt-2">
                                    <div className="flex justify-between">
                                      <span className="font-semibold text-gray-800">{t('inquiries.details.yourEarningsColon')}</span>
                                      <span className="font-bold text-green-600 text-lg">₱{breakdown.netEarnings}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            // Client view - show total payment with platform fee
                            (() => {
                              const breakdown = calculatePaymentBreakdown(serviceAmount);
                              return (
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{isJobInquiry ? t('inquiries.details.bidAmountColon') : t('inquiries.details.serviceAmountColon')}</span>
                                    <span className="text-gray-800">₱{breakdown.baseAmount}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('inquiries.details.platformFee', { rate: breakdown.feeRate })}</span>
                                    <span className="text-gray-600">+₱{breakdown.platformFee}</span>
                                  </div>
                                  <div className="border-t pt-2 mt-2">
                                    <div className="flex justify-between">
                                      <span className="font-semibold text-gray-800">{t('inquiries.details.totalToPay')}</span>
                                      <span className="font-bold text-blue-600 text-lg">₱{breakdown.total}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedSupportTicket ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{t('inquiries.details.supportTicket')}</h3>
                  <p className="text-sm text-gray-500 mb-1">{selectedSupportTicket.subject}</p>
                  <p className="text-xs text-gray-400 mb-4">#{selectedSupportTicket.id?.slice(0, 8).toUpperCase()}</p>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${selectedSupportTicket.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {selectedSupportTicket.status}
                  </span>
                  <p className="text-xs text-gray-400 mt-4">{t('inquiries.details.created')} {new Date(selectedSupportTicket.created_at).toLocaleDateString()}</p>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {t('inquiries.details.selectConvDetails')}
                </div>
              )}
            </div>
            </div>{/* end mobile slide-in panel */}
          </div>
        )}
      </div>

      {/* Modify Modal */}
      {showModifyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('inquiries.details.modifyBooking')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('inquiries.details.proposedAmount')}
                </label>
                <input
                  type="number"
                  value={modifyData.proposed_amount}
                  onChange={(e) => setModifyData(prev => ({ ...prev, proposed_amount: e.target.value }))}
                  placeholder={t('inquiries.details.enterNewAmount')}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('inquiries.details.proposedDate')}
                </label>
                <input
                  type="date"
                  value={modifyData.proposed_date}
                  onChange={(e) => setModifyData(prev => ({ ...prev, proposed_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('inquiries.details.proposedTime')}
                </label>
                <input
                  type="time"
                  value={modifyData.proposed_time}
                  onChange={(e) => setModifyData(prev => ({ ...prev, proposed_time: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('inquiries.details.notesMessage')}
                </label>
                <textarea
                  value={modifyData.notes}
                  onChange={(e) => setModifyData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder={t('inquiries.details.explainChanges')}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModifyModal(false);
                  setModifyData({ proposed_amount: '', proposed_date: '', proposed_time: '', notes: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                {t('inquiries.details.cancel')}
              </button>
              <button
                onClick={handleModifySubmit}
                disabled={processingAction}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {processingAction ? t('inquiries.details.sending') : t('inquiries.details.sendProposal')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Conversation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{t('inquiries.details.deleteConversation')}</h2>
              <p className="text-gray-500 mt-2">
                {t('inquiries.details.deleteConvDesc')}
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingConversation}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                {t('inquiries.details.cancel')}
              </button>
              <button
                onClick={handleDeleteConversation}
                disabled={deletingConversation}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
              >
                {deletingConversation ? t('inquiries.details.deleting') : t('inquiries.details.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Violation Warning Modal */}
      {showViolationModal && violationWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              {violationWarning.suspended ? (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
              ) : violationWarning.count >= 2 ? (
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              ) : (
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              )}
              
              <h2 className={`text-xl font-bold ${violationWarning.suspended ? 'text-red-600' : violationWarning.count >= 2 ? 'text-red-600' : 'text-yellow-600'}`}>
                {violationWarning.suspended ? t('inquiries.details.accountSuspended') : violationWarning.count >= 2 ? t('inquiries.details.finalWarning') : t('inquiries.details.policyViolation')}
              </h2>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
              <div className="text-sm text-gray-700 whitespace-pre-line">
                {violationWarning.message}
              </div>
            </div>
            
            {!violationWarning.suspended && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-sm text-gray-500">{t('inquiries.details.violationCount')}</span>
                <div className="flex gap-1">
                  {[1, 2, 3].map((num) => (
                    <div
                      key={num}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        num <= violationWarning.count 
                          ? 'bg-red-500 text-white' 
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {num}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <button
              onClick={() => {
                setShowViolationModal(false);
                if (violationWarning.suspended) {
                  // Redirect to home if suspended
                  navigate('/');
                }
              }}
              className={`w-full px-4 py-3 rounded-lg font-medium ${
                violationWarning.suspended 
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {violationWarning.suspended ? t('inquiries.details.iUnderstand') : t('inquiries.details.gotIt')}
            </button>
          </div>
        </div>
      )}

      <Footer />

      {/* Full-screen image viewer — fits any image within the screen */}
      {viewerImage && (
        <div
          className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setViewerImage(null)}
          data-testid="image-viewer-overlay"
        >
          <button
            onClick={() => setViewerImage(null)}
            className="absolute top-4 right-4 bg-white/15 hover:bg-white/30 text-white rounded-full p-2 transition"
            aria-label="Close image"
            data-testid="image-viewer-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={viewerImage}
            alt="Full size"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="image-viewer-img"
          />
        </div>
      )}
    </div>
  );
};

export default Inquiries;
